import { useCallback, useState } from "react"
import { zip } from "fflate"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"

export function useDocsCsvExport() {
	const [isLoading, setIsLoading] = useState(false)

	const exportMutation = api.workspace.exportArticles.useMutation({
		onSuccess: (data) => {
			const blob = new Blob([data.csvData], { type: "text/csv;charset=utf-8;" })
			const link = document.createElement("a")
			const url = URL.createObjectURL(blob)
			link.setAttribute("href", url)
			link.setAttribute(
				"download",
				`docs-export-${new Date().toISOString().split("T")[0]}.csv`,
			)
			link.style.visibility = "hidden"
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			URL.revokeObjectURL(url)

			toast.success({
				title: "Docs exported successfully",
				description: "Your docs data has been downloaded as a CSV file.",
			})
		},
		onError: (error) => {
			toast.error({
				title: "Export failed",
				description:
					error.message || "Failed to export docs data. Please try again.",
			})
		},
	})

	const exportNow = useCallback(async () => {
		setIsLoading(true)
		try {
			await exportMutation.mutateAsync({})
		} finally {
			setIsLoading(false)
		}
	}, [exportMutation])

	return { exportNow, isLoading }
}

export function useDocsPdfExport() {
	const [isLoading, setIsLoading] = useState(false)

	const exportMutation = api.workspace.exportDocsPdf.useMutation({
		onSuccess: (data) => {
			const binary = window.atob(data.data)
			const bytes = new Uint8Array(binary.length)
			for (let i = 0; i < binary.length; i++) {
				bytes[i] = binary.charCodeAt(i)
			}
			const blob = new Blob([bytes], { type: "application/pdf" })
			const link = document.createElement("a")
			const url = URL.createObjectURL(blob)
			link.setAttribute("href", url)
			link.setAttribute("download", data.filename)
			link.style.visibility = "hidden"
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			URL.revokeObjectURL(url)

			const isPartial = data.articleCount < data.totalArticles
			toast.success({
				title: "Docs exported successfully",
				description: isPartial
					? `${data.articleCount} of ${data.totalArticles} articles are in this PDF. Export a single collection to get the rest.`
					: "Your documentation has been downloaded as a PDF.",
			})
		},
		onError: (error) => {
			toast.error({
				title: "Export failed",
				description:
					error.message || "Failed to export docs as PDF. Please try again.",
			})
		},
	})

	const exportNow = useCallback(async () => {
		setIsLoading(true)
		try {
			await exportMutation.mutateAsync({})
		} finally {
			setIsLoading(false)
		}
	}, [exportMutation])

	return { exportNow, isLoading }
}

export function useDocsMdExport() {
	const [isLoading, setIsLoading] = useState(false)

	const exportMutation = api.workspace.exportArticlesMarkdown.useMutation({
		onSuccess: (data) => {
			const zipInput: Record<string, Uint8Array> = {}
			for (const file of data.files) {
				const encoder = new TextEncoder()
				zipInput[file.path] = encoder.encode(file.content)
			}

			zip(zipInput, (err: Error | null, compressed: Uint8Array) => {
				if (err) {
					toast.error({
						title: "Export failed",
						description: "Failed to create zip file.",
					})
					setIsLoading(false)
					return
				}

				const blob = new Blob([compressed.buffer as ArrayBuffer], {
					type: "application/zip",
				})
				const link = document.createElement("a")
				const url = URL.createObjectURL(blob)
				link.setAttribute("href", url)
				link.setAttribute("download", data.zipFilename)
				link.style.visibility = "hidden"
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				URL.revokeObjectURL(url)

				setIsLoading(false)
				toast.success({
					title: "Docs exported successfully",
					description:
						"Your docs have been downloaded as a zip with Markdown files.",
				})
			})
		},
		onError: (error) => {
			toast.error({
				title: "Export failed",
				description:
					error.message || "Failed to export docs data. Please try again.",
			})
			setIsLoading(false)
		},
	})

	const exportNow = useCallback(async () => {
		setIsLoading(true)
		try {
			await exportMutation.mutateAsync({})
		} catch {
			setIsLoading(false)
		}
	}, [exportMutation])

	return { exportNow, isLoading }
}
